'use client'

import Head from 'next/head'
import Link from 'next/link'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { Card } from '~/components/Card'

import {
  DashboardEvaluation,
  DashboardTable,
} from '~/components/Dashboard/DashboardTable'
import { useWorkspaceList } from './dashboardQuery'
import SpinnerCircle from '~/components/SpinnerCircle'
import {
  useDashboardEvaluationsContext,
  useDashboardWorkspaceContext,
} from './dashboardContext'
import { DashboardChecklist } from '~/components/Dashboard/DashboardChecklist'
import {
  StatusValue,
  getCurrentEvaluationStatus,
} from '~/components/Evaluation/EvaluationMenu/mutations'
import { UnderlinedTab } from './_components/UnderlinedTab'
import { isWorkspaceMemberViewer } from './workspace/_utils'
import { useSwitchWorkspaceFromQueryParams } from '~/hooks/useSwitchWorkspaceFromQueryParams'
import { useTranslation } from '~/i18n/useTranslation'
import type { TranslationKey } from '~/i18n/translations'

const shouldDebug = () => {
  // Check NEXT_PUBLIC_ENV (used in docker) or NEXT_PUBLIC_VERCEL_ENV (used in Vercel)
  const env = process.env.NEXT_PUBLIC_ENV || process.env.NEXT_PUBLIC_VERCEL_ENV
  const nodeEnv = process.env.NODE_ENV

  return (
    env === 'dev' ||
    env === 'staging' ||
    env === 'development' ||
    env === 'preview' ||
    nodeEnv === 'development'
  )
}

const tabKeys: { key: 'owned' | 'shared' | 'archived'; labelKey: TranslationKey }[] = [
  { key: 'owned', labelKey: 'dashboard.myEvaluations' },
  { key: 'shared', labelKey: 'dashboard.shared' },
  { key: 'archived', labelKey: 'dashboard.archived' },
]

export default function DashboardPage() {
  const { t } = useTranslation()
  const [dashboardTab, setDashboardTab] = useState<
    'owned' | 'shared' | 'archived'
  >('owned')

  const {
    member,
    memberId,
    status: workspaceStatus,
  } = useDashboardWorkspaceContext()
  const dashboardEvaluationsContext = useDashboardEvaluationsContext()

  // Move debug logging to useEffect to prevent logging on every render
  useEffect(() => {
    if (shouldDebug()) {
      console.log(
        `[DEBUG] DashboardPage: Contexts loaded, evaluationsStatus=${
          dashboardEvaluationsContext.status
        }, timestamp=${new Date().toISOString()}`
      )
    }
  }, [dashboardEvaluationsContext.status])

  // Always call ALL hooks before any early returns to maintain hook order
  // Use memberId from context (from useAppliedUserId) - this is the authoritative member ID
  // The backend's get_applied_user_id reads member_id directly from the JWT token
  // Pass empty string if memberId not available to disable the query
  const memberIdForQuery = memberId || ''
  const workspaceListResponse = useWorkspaceList(memberIdForQuery)

  // Switch dashboard if workspace_id is in the query params (must be called before early return)
  useSwitchWorkspaceFromQueryParams()

  // These hooks must be called before early return to maintain hook order
  const hasWorkspace = useCallback(
    (workspaceID) =>
      workspaceListResponse.data?.getWorkspacesForMember?.find(
        (workspace) => workspace.id === workspaceID
      ),
    [workspaceListResponse.data, dashboardEvaluationsContext.workspace.id]
  )

  const evaluations = useMemo(
    () =>
      // seperate owned and shared evaluations
      dashboardEvaluationsContext.evaluations?.reduce(
        (accumulated, evaluation) => {
          const { value: currentStatus } = getCurrentEvaluationStatus(
            evaluation.status
          )
          if (currentStatus === StatusValue.deleted) {
            return accumulated
          }

          const isOwned =
            evaluation.workspace?.id ===
            dashboardEvaluationsContext.workspace.id

          if (isOwned && currentStatus === StatusValue.archived) {
            accumulated.archived.push(evaluation)
            return accumulated
          }

          if (isOwned) {
            accumulated.owned.push(evaluation)
            return accumulated
          }

          if (!hasWorkspace(evaluation.workspace?.id)) {
            accumulated.shared.push(evaluation)
            return accumulated
          }

          return accumulated
        },
        {
          owned: [] as DashboardEvaluation[],
          shared: [] as DashboardEvaluation[],
          archived: [] as DashboardEvaluation[],
        }
      ),
    [
      dashboardEvaluationsContext.evaluations,
      dashboardEvaluationsContext.workspace.id,
      dashboardTab,
      hasWorkspace,
    ]
  )

  // Show loading state if memberId is not available yet (after all hooks)
  if (!memberId || workspaceStatus === 'loading') {
    return (
      <>
        <Head>
          <title>Dashboard | Taloflow</title>
        </Head>
        <div className="flex justify-center items-center min-h-[400px] w-full">
          <SpinnerCircle />
        </div>
      </>
    )
  }

  return (
    <>
      <Head>
        <title>Dashboard | Taloflow</title>
      </Head>
      <div className="hidden md:block max-w-[1410px]">
        <div className="mb-6">
          <h2 className="text-gray-700 underline-offset-[12px] tracking-wide underline decoration-primary-500 font-semibold">
            {t('dashboard.gettingStarted')}
          </h2>
        </div>
        <DashboardChecklist />
      </div>
      <div className="w-full flex flex-col-reverse gap-4 md:gap-0 md:flex-row py-3 md:items-center my-4">
        <div className="flex gap-2.5">
          {tabKeys.map((tab) => (
            <UnderlinedTab
              key={tab.key}
              active={dashboardTab === tab.key}
              onClick={() => setDashboardTab(tab.key)}
              badgeText={evaluations?.[tab.key]?.length?.toString()}
            >
              {t(tab.labelKey)}
            </UnderlinedTab>
          ))}
        </div>
        <div className="md:ml-auto">
          {!isWorkspaceMemberViewer(member) ? (
            <Link
              href="/evaluations/new"
              className="bg-primary-500 rounded-[.625rem] px-7 py-3 text-white font-semibold text-sm flex items-center justify-center"
            >
              {t('dashboard.createEvaluation')}
            </Link>
          ) : null}
        </div>
      </div>
      <Card color="light-gray" className="px-8 py-8">
        {dashboardEvaluationsContext.status === 'success' && evaluations ? (
          <DashboardTable ownership={dashboardTab} evaluations={evaluations} />
        ) : (
          <div className="flex justify-center">
            <SpinnerCircle />
          </div>
        )}
      </Card>
    </>
  )
}
